import { Flag, Quote } from "lucide-react";
import { SURVEY_AUDIENCES, SURVEY_LABELS } from "../../../lib/surveys";

const Stat = ({ label, value, sub }) => (
  <div className="rounded-xl border border-border bg-bone px-4 py-3">
    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-olive">{label}</p>
    <p className="mt-1 font-serif text-3xl leading-none text-ink">{value}</p>
    {sub && <p className="mt-1 text-[12px] text-olive">{sub}</p>}
  </div>
);

const Bar = ({ count, total }) => {
  const pct = total ? Math.round((100 * count) / total) : 0;
  return (
    <span className="inline-flex items-center gap-2">
      <span className="inline-block h-2 w-28 overflow-hidden rounded-full bg-ink/10"><span className="block h-2 rounded-full bg-tangerine" style={{ width: `${pct}%` }} /></span>
      <span className="text-[12px] text-olive">{count} · {pct}%</span>
    </span>
  );
};

const QuestionStats = ({ q, highlight }) => {
  if (q.average !== undefined) {
    return (
      <div className="py-3">
        <p className="text-[13px] text-olive">{q.label}</p>
        <p className="mt-1 font-serif text-2xl text-ink">
          {q.average}<span className="text-[12px] text-olive"> / {q.type === "nps" ? 10 : 5} · {q.answered} answered{q.nps !== undefined && <> · NPS <strong className="text-ink">{q.nps}</strong></>}</span>
        </p>
      </div>
    );
  }
  if (q.counts) {
    return (
      <div className="py-3">
        <p className="text-[13px] text-olive">{q.label}</p>
        <ul className="mt-2 space-y-1">
          {Object.entries(q.counts).map(([k, v]) => (
            <li key={k} className="flex flex-wrap items-center justify-between gap-2 text-sm text-ink"><span>{k}</span><Bar count={v} total={q.answered} /></li>
          ))}
        </ul>
      </div>
    );
  }
  return (
    <div className="py-3">
      <p className="text-[13px] text-olive">{q.label} · {q.comments.length} comment{q.comments.length === 1 ? "" : "s"}</p>
      <ul className="mt-2 space-y-2">
        {q.comments.map((c) => (
          <li
            key={c.response_id}
            id={`response-${c.response_id}`}
            data-testid={`survey-comment-${c.response_id}`}
            className={`scroll-mt-28 rounded-md border-l-4 bg-sand px-4 py-2.5 text-[15px] leading-relaxed text-ink ${c.flags.length ? "border-scarlet" : "border-tangerine"} ${highlight === c.response_id ? "ring-2 ring-yellow" : ""}`}
          >
            “{c.text}”
            <span className="ml-2 inline-flex flex-wrap gap-1.5 align-middle">
              {c.flags.map((f) => <span key={f} className="inline-flex items-center gap-1 rounded bg-scarlet/10 px-1.5 py-0.5 text-[11px] text-scarlet"><Flag className="h-3 w-3" /> {f.replace("_", " ")}</span>)}
              {c.quote_ok && <span className="inline-flex items-center gap-1 rounded bg-grass/15 px-1.5 py-0.5 text-[11px] text-grass"><Quote className="h-3 w-3" /> ok to quote{c.name ? ` · ${c.name}` : ""}</span>}
            </span>
            <span className="ml-2 text-[11px] text-olive">{new Date(c.submitted_at).toLocaleDateString()}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

/** Numbers + comments for each survey over the selected window. */
export const FeedbackSummary = ({ summary, highlight }) => {
  if (!summary) return null;
  const a = summary.audiences;
  const flagged = (x) => (x?.flagged ? `${x.flagged} flagged` : "none flagged");
  return (
    <div className="space-y-8" data-testid="feedback-summary">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label={`Responses · ${summary.days}d`} value={summary.total} sub={`${summary.all_time} all time`} />
        {SURVEY_AUDIENCES.map((aud) => <Stat key={aud} label={SURVEY_LABELS[aud]} value={a[aud]?.count ?? 0} sub={flagged(a[aud])} />)}
      </div>
      {SURVEY_AUDIENCES.map((aud) => {
        const s = a[aud] || { count: 0, questions: [] };
        return (
          <section key={aud} data-testid={`feedback-summary-${aud}`}>
            <h4 className="font-serif text-2xl text-ink">{SURVEY_LABELS[aud]}</h4>
            {s.count === 0 ? (
              <p className="mt-2 text-sm text-olive">No responses in this window.</p>
            ) : (
              <div className="mt-2 divide-y divide-ink/10 rounded-xl border border-border bg-bone px-5">
                {s.questions.map((q) => <QuestionStats key={q.id} q={q} highlight={highlight} />)}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
};
